/**
 * A safe wrapper around window.localStorage and window.sessionStorage.
 * In sandboxed iframes (e.g. inside Electron preview apps lacking allow-same-origin),
 * accessing localStorage/sessionStorage throws a SecurityError.
 * This helper falls back to a temporary in-memory store if storage is blocked.
 */

class MemoryStorage implements Storage {
  private store: Record<string, string> = {};

  get length(): number {
    return Object.keys(this.store).length;
  }

  clear(): void {
    this.store = {};
  }

  getItem(key: string): string | null {
    return key in this.store ? this.store[key] : null;
  }

  key(index: number): string | null {
    const keys = Object.keys(this.store);
    return keys[index] || null;
  }

  removeItem(key: string): void {
    delete this.store[key];
  }

  setItem(key: string, value: string): void {
    this.store[key] = String(value);
  }
}

function getSafeStorage(type: "localStorage" | "sessionStorage"): Storage {
  try {
    const storage = window[type];
    const testKey = "__storage_test__";
    storage.setItem(testKey, "1");
    storage.removeItem(testKey);
    return storage;
  } catch {
    console.warn(`[SafeStorage] ${type} is not accessible. Using in-memory fallback.`);
    return new MemoryStorage();
  }
}

export const safeLocalStorage = getSafeStorage("localStorage");
export const safeSessionStorage = getSafeStorage("sessionStorage");
